import React, { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import Button, { ButtonTypes } from "../../../../components/Button";
import {
  Field,
  FieldHelper,
  FieldLabel,
} from "../../../../components/FormPanel";
import { FormMoneyInput, InputDate } from "../../../../components/Input";
import { ConfirmPopUp } from "../../../../components/PopUp";
import { localToday, toIsoDate } from "../../../../utils/dates";
import {
  validateOverrideDate,
  validateOverrideFxRate,
  validateOverridePrice,
} from "../../../Investments/panels/overrideValidation";
import { isPairAssetType } from "./constants";
import {
  EditorActions,
  EditorError,
  EditorFields,
  EditorForm,
  FixSection,
  FixSectionTitle,
} from "./styles";
import {
  ManualPriceEntry,
  ManualPricingSectionProps,
  ManualPricingValues,
} from "./types";

const toEntry = ({
  date,
  priceNative,
  fxToEur,
}: ManualPricingValues): ManualPriceEntry | null => {
  if (!date || priceNative === "") {
    return null;
  }
  return {
    date: toIsoDate(date),
    priceNative,
    ...(fxToEur === "" ? {} : { fxToEur }),
  };
};

const dayOf = (date: Date | null) => date && toIsoDate(date);

const isSameEntry = (a: ManualPricingValues, b: ManualPricingValues) =>
  dayOf(a.date) === dayOf(b.date) &&
  a.priceNative === b.priceNative &&
  a.fxToEur === b.fxToEur;

export const ManualPricingSection: React.FC<ManualPricingSectionProps> = ({
  instrument,
  isSubmitting,
  error,
  draft,
  onDraftChange,
  onConfirm,
}) => {
  const { symbol, nativeCurrency, yahooSymbol, assetType, manualPricingOnly } =
    instrument;
  const [pendingEntry, setPendingEntry] = useState<ManualPriceEntry | null>(
    null,
  );
  const [pristineValues] = useState<ManualPricingValues>(() => ({
    date: localToday(),
    priceNative: "",
    fxToEur: "",
  }));
  const { control, handleSubmit, getValues } = useForm<ManualPricingValues>({
    defaultValues: draft ?? pristineValues,
  });
  const watchedValues = useWatch({ control });

  useEffect(() => {
    const values = getValues();
    onDraftChange?.(isSameEntry(values, pristineValues) ? null : values);
  }, [watchedValues, pristineValues, getValues, onDraftChange]);

  const showFxRate = nativeCurrency !== "EUR";
  const yahooNote = yahooSymbol
    ? ` Its Yahoo symbol ${yahooSymbol} will be removed.`
    : "";
  const historyNote =
    yahooSymbol || isPairAssetType(assetType)
      ? " Its provider price history will be removed."
      : "";
  const actionLabel = manualPricingOnly ? "Record price" : "Use manual pricing";
  const confirmTitle = manualPricingOnly
    ? `Record this price for ${symbol}?`
    : `Use manual pricing for ${symbol}?`;
  const confirmText = manualPricingOnly
    ? `Every holder of ${symbol} will be revalued from this price.`
    : `Every holder of ${symbol} will be valued from manually entered prices and it will stop receiving automatic prices.${yahooNote}${historyNote}`;

  const review = (values: ManualPricingValues) =>
    setPendingEntry(toEntry(values));
  const handleConfirmOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setPendingEntry(null);
    }
  };
  const handleConfirm = () => {
    if (pendingEntry) {
      onConfirm(pendingEntry);
    }
    setPendingEntry(null);
  };

  return (
    <FixSection aria-label="Manual pricing">
      <FixSectionTitle>Manual pricing</FixSectionTitle>
      <EditorForm noValidate onSubmit={handleSubmit(review)}>
        <EditorFields>
          <Field>
            <FieldLabel htmlFor="instrument-fix-price-date">
              Price date
            </FieldLabel>
            <InputDate
              id="instrument-fix-price-date"
              name="date"
              control={control}
              rules={{ validate: validateOverrideDate }}
              disabled={isSubmitting}
              maxDate={localToday()}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="instrument-fix-price">
              {`Price (${nativeCurrency})`}
            </FieldLabel>
            <FormMoneyInput
              id="instrument-fix-price"
              name="priceNative"
              control={control}
              rules={{ validate: validateOverridePrice }}
              disabled={isSubmitting}
              placeholder="0.00"
            />
          </Field>
          {showFxRate && (
            <Field>
              <FieldLabel htmlFor="instrument-fix-fx-rate">
                FX rate to EUR (optional)
              </FieldLabel>
              <FormMoneyInput
                id="instrument-fix-fx-rate"
                name="fxToEur"
                control={control}
                rules={{ validate: validateOverrideFxRate }}
                disabled={isSubmitting}
                placeholder="Optional when PMA can find a rate"
              />
              <FieldHelper>
                Enter a rate only when automatic conversion is unavailable.
              </FieldHelper>
            </Field>
          )}
        </EditorFields>
        {error && <EditorError role="alert">{error}</EditorError>}
        <EditorActions>
          <Button
            type={ButtonTypes.primary}
            buttonType="submit"
            isLoading={isSubmitting}
          >
            {actionLabel}
          </Button>
        </EditorActions>
      </EditorForm>
      <ConfirmPopUp
        isOpen={pendingEntry !== null}
        setIsOpen={handleConfirmOpenChange}
        title={confirmTitle}
        text={confirmText}
        confirmLabel={actionLabel}
        onConfirm={handleConfirm}
      />
    </FixSection>
  );
};
